import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { VerificationDatabase } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

interface DatabaseVerificationProps extends VerificationDetailProps {
  verification: VerificationDatabase;
}

const DatabaseVerification = ({ verification }: DatabaseVerificationProps) => {
  const {
    'name-first': nameFirst,
    'name-middle': nameMiddle,
    'name-last': nameLast,
    birthdate,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'country-code': countryCode,
    status,
    'redacted-at': redactedAt,
    'identification-number': identificationNumber,
  } = verification.attributes;

  const isRedacted = redactedAt != null;

  const hasBeenSubmitted = status !== VerificationStatus.Initiated;

  const checks = verification.attributes.checks;
  const nameFull = [nameFirst, nameMiddle, nameLast].filter(Boolean).join(' ');

  const showFullSSN = usePermission(Permission.VerificationDatabaseSsnView);
  const permissionedSSN = showFullSSN
    ? identificationNumber
    : identificationNumber?.substring(identificationNumber?.length - 4);

  // For some legacy verifications, we did not store the responses from our vendors, so we
  // do not currently have a way of restoring verifications checks.
  return (
    <>
      {hasBeenSubmitted && checks && checks.length > 0 ? (
        <ChecksTable checks={checks} verification={verification} />
      ) : (
        <StyledAttributeGrid>
          <Attribute label='Full name' value={nameFull} redacted={isRedacted} />
          <Attribute label='Birthdate' value={formatDate(birthdate)} redacted={isRedacted} />
          <Attribute label='SSN' value={permissionedSSN} redacted={isRedacted} toggleable />
          <Attribute
            label='Address'
            value={[
              addressStreet1,
              addressStreet2,
              [[addressCity, addressSubdivision].filter(Boolean).join(' '), addressPostalCode]
                .filter(Boolean)
                .join(', '),
            ]}
            redacted={isRedacted}
          />
        </StyledAttributeGrid>
      )}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {['US', 'PR'].includes(countryCode) && (
        <>
          <Spacer height={2} />
          <Paragraph>
            Results shall only be used for Gramm-Leach-Bliley Act (GLBA) permissible use cases such
            as identity verification for the purpose of fraud prevention. Additionally, the
            information provided does not constitute and may not be used as a &ldquo;consumer
            report&rdquo; as is defined in the Fair Credit Reporting Act (&ldquo;FCRA&rdquo;).
          </Paragraph>
        </>
      )}
    </>
  );
};

export default DatabaseVerification;
